import { ReactNode } from 'react';

import { Text, TextLevels } from '@bytel/trilogy-react';

import { FundingType } from '@app-types/funding';

import { InlinePrice } from '@components/inline-price';

import { hasInstallment, isComptant, isEDP } from '@helpers/funding';
import { formatPrice } from '@helpers/wording';

import { PricesDetails } from '@product-detail/helpers';

import { OdrInfos } from './odr-infos';

export function getFundingSubtitle(
    funding: FundingType,
    installmentFundings: FundingType[],
    hasOdr: boolean,
    prices: PricesDetails,
    isCurrentFunding: boolean,
) {
    const getInstallmentSubtitle = (): string => {
        const durations = installmentFundings.map((funding) => funding.installmentCount).sort((a, b) => a - b);

        if (durations.length === 1) {
            return `Le reste en ${durations[0]} mois`;
        }
        if (durations.length === 2) {
            return `Le reste en ${durations[0]} ou ${durations[1]} mois`;
        }

        const lastDuration = durations.pop();
        const joinedDurations = durations.join(', ');
        return `Le reste en ${joinedDurations} ou ${lastDuration} mois`;
    };

    let subtitle: ReactNode | string = '';
    if (hasInstallment(funding.type)) {
        subtitle = getInstallmentSubtitle();
    } else if (isEDP(funding.type)) {
        subtitle = (
            <>
                + {formatPrice(funding.monthlyAmount)}/mois pendant {funding.installmentCount} mois{' '}
                {funding.interestAmount > 0 ? 'avec' : 'sans'} frais
            </>
        );
    } else if (isComptant(funding.type)) {
        subtitle = 'En une seule fois';
    }

    return (
        <>
            {subtitle}
            {hasOdr && !hasInstallment(funding.type) && isCurrentFunding ? (
                <OdrInfos
                    odrRefund={Math.abs(prices.productPrices.odrRefund)}
                    phonePriceWithOdrDeducted={prices.productPrices.marketingAmount}
                />
            ) : null}
        </>
    );
}

export function FundingRadioInstallmentSubtitle({
    funding,
    prices,
    hasOdr,
    currentFunding,
}: {
    funding: FundingType;
    prices: PricesDetails;
    hasOdr: boolean;
    currentFunding?: FundingType;
}) {
    return (
        <>
            <Text marginless level={TextLevels.TWO}>
                <InlinePrice amount={funding.initialContributionWithOdr || funding.initialDeposit} />
            </Text>
            <Text level={TextLevels.TWO}>
                + {formatPrice(funding.monthlyAmount)}/mois pendant {funding.installmentCount} mois{' '}
                {funding.interestAmount > 0 ? 'avec' : 'sans'}&nbsp;frais
            </Text>
            {hasOdr && currentFunding?.type === funding.type ? (
                <OdrInfos
                    odrRefund={Math.abs(prices.productPrices.odrRefund)}
                    phonePriceWithOdrDeducted={prices.productPrices.marketingAmount}
                />
            ) : null}
        </>
    );
}

export function getFundingRadioLabel(
    funding: FundingType,
    minimumInitialContribution: number,
    isInstallmentType: boolean,
): string {
    if (isInstallmentType) {
        return `À partir de ${formatPrice(minimumInitialContribution)}`;
    } else {
        return `${formatPrice(funding.initialContributionWithOdr || funding.initialDeposit)}`;
    }
}
